import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import { useEffect, useState } from "react";
import Spinner from "../../components/ui/Spinner";
import {
    API_BASE_URL,
    DEFAULT_PAGE_DESCRIPTION,
    DEFAULT_PAGE_TITLE,
} from "../../utilities/constants";
import { NavLink, useLocation, useParams } from "react-router";
import useQueryParams from "../../hooks/useQueryParams";
import { dobToAge, formatHeightUnit, handleApiCall, jsonToQueryParams } from "../../utilities/helper";
import { useUI } from "../../context/UI";

const CustomerDetails = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(false);
    const { queryParams } = useQueryParams();
    const location = useLocation();
    const { id } = useParams();
    const {
        setSegmentTitle
    } = useUI();
    const [controller, setController] = useState(null);

    const callListApi = async () => {
        controller?.abort();
        const newController = new AbortController();
        setController(newController);
        const { signal } = newController;
        setLoading(true);
        try {
            const query = jsonToQueryParams({
                search: queryParams.get("search") || "",
            });
            const response = await handleApiCall(
                `${API_BASE_URL}/api/1.0.0/customer/v2/${id}`,
                {
                    method: "GET"
                },
                null,
                signal
            );
            if (response.responseStatus == true) {
                setData(response.data);
            } else {
                setData(null);
            }
            if (response.responseCode !== 299) {
                setLoading(false);
            }
        } catch (e) {
            console.error("Error", e);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        callListApi();
        setSegmentTitle("Customer Details");
    }, [location.search]);


    return (
        <>
            <PageMeta
                title={`Customer Details - ${DEFAULT_PAGE_TITLE}`}
                description={`Customer Details - ${DEFAULT_PAGE_DESCRIPTION}`}
            />
            <div className="flex justify-between items-center mb-4">
                <PageBreadcrumb
                    pageTitle="Customer Details"
                    breadcrumbs={[
                        { label: "Customers", path: "/customers" },
                        { label: "Customer Details" }
                    ]}
                />
            </div>
            {loading ? (
                <div className="flex justify-center items-center">
                    <Spinner />
                </div>
            ) : (
                <div className="p-6 bg-white dark:bg-gray-800 rounded-lg mx-auto h-full">
                    <div className="flex flex-col md:flex-row gap-8">
                        <div className="flex flex-col items-center md:items-start gap-6 w-full md:w-1/4">
                            <div className="w-40 h-40 overflow-hidden rounded-lg  bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                <img
                                    src={
                                        data?.profile?.profile?.profileDpSignedUrl
                                            ? data.profile?.profile?.profileDpSignedUrl
                                            : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                                `${data?.profile?.firstName || ""} ${data?.profile?.lastName || ""}`
                                            )}&background=a8936a&color=fff`
                                    }
                                    alt="Profile"
                                    className="w-full h-full object-scale-down"
                                />
                            </div>
                            <div className="space-y-4 w-full">
                                <div>
                                    <h3 className="text-sm text-gray-500 dark:text-gray-400">Full Name</h3>
                                    <p className="text-base font-medium text-gray-900 dark:text-gray-100">
                                        <span
                                            className="max-w-[180px] block truncate"
                                            title={`${data?.profile?.firstName || ""} ${data?.profile?.lastName || ""}`.trim()}
                                        >
                                            {(() => {
                                                const fullName = `${data?.profile?.firstName || ""} ${data?.profile?.lastName || ""}`.trim();
                                                return fullName.length > 30 ? fullName.slice(0, 30) + "…" : fullName;
                                            })()}
                                        </span>
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-sm text-gray-500 dark:text-gray-400">Email</h3>
                                    <p className="text-base font-medium text-gray-900 dark:text-gray-100">
                                        <span
                                            className="max-w-[220px] block truncate"
                                            title={`${data?.profile?.email || ""}`.trim()}
                                        >
                                            {(() => {
                                                const Emails = `${data?.profile?.email || ""}`.trim();
                                                return Emails.length > 25 ? Emails.slice(0, 25) + "…" : Emails;
                                            })()}
                                        </span>
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-sm text-gray-500 dark:text-gray-400">Gender</h3>
                                    <p className="text-base font-medium text-gray-900 dark:text-gray-100">
                                        {data?.profile?.profile?.gender}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="flex-1 md:pl-8 md:border-l border-gray-200 dark:border-gray-700">
                            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
                                Measurements
                            </h2>

                            <div className="grid grid-cols-2 sm:grid-cols-2 gap-6">
                                <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                                    <h3 className="text-sm text-gray-500 dark:text-gray-400">Age</h3>
                                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                        {dobToAge(data?.profile?.profile?.dob)} <span className="text-sm font-normal">YEARS</span>
                                    </p>
                                </div>

                                <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                                    <h3 className="text-sm text-gray-500 dark:text-gray-400">Height</h3>
                                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                        {data?.profile?.profile?.height || 0} <span className="text-sm font-normal">{formatHeightUnit(data?.profile?.profile?.heightUnit) || "CM"}</span>
                                    </p>
                                </div>

                                <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                                    <h3 className="text-sm text-gray-500 dark:text-gray-400">Weight</h3>
                                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                        {data?.profile?.profile?.weight || 0} <span className="text-sm font-normal">{data?.profile?.profile?.weightUnit || "KG"}</span>
                                    </p>
                                </div>

                                <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                                    <h3 className="text-sm text-gray-500 dark:text-gray-400">Shoe Size</h3>
                                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                        {data?.profile?.profile?.shoeSize || 0} <span className="text-sm font-normal">{data?.profile?.profile?.shoeSizeUnit || "CM"}</span>
                                    </p>
                                </div>
                                <div className="p-4 rounded-lg">
                                   
                                </div>
                                <div className="flex justify-end items-center mb-4 ">
                                    <NavLink to={`/look-details/${data?.profile?.id}`} state={{ data }}>
                                        <button
                                            type="submit"
                                            className="bg-yellow-600 text-white px-6 py-2 rounded-md hover:bg-yellow-700 transition"
                                        >
                                            Your Looks
                                        </button>
                                    </NavLink>
                                    {/* <NavLink to={`/customers`}>
                                         <ButtonText
                                         type="submit"
                                            title='Generate Looks'
                                        />
                                       </NavLink> */}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>


            )}

        </>
    );
};
export default CustomerDetails;